import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { isCustomer, venueImageUrl } from '../../api';
import { useGeolocation } from '../../hooks/useGeolocation';
import { useLocationFilter } from '../../hooks/useLocationFilter';
import LocationFilter from '../../components/LocationFilter';
import { trackEvent } from '../../analytics';
import { useFeatures } from '../../features';
import { toApiError } from '../hangouts/api';
import { loginUrlFor } from '../hangouts/utils';
import {
  bookPoolParty,
  cancelPoolPartyBooking,
  getMyPoolPartyBookings,
  getPoolParties,
  getPoolParty,
  partyPrice,
  partyWhen,
  PoolPartyBooking,
  PublicPoolParty
} from './api';
import './poolParties.css';

type Msg = { kind: 'ok' | 'err' | 'info'; text: string } | null;

const Message = ({ msg }: { msg: Msg }) =>
  msg ? <div className={`pool-msg ${msg.kind}`} role={msg.kind === 'err' ? 'alert' : 'status'}>{msg.text}</div> : null;

function Spots({ party }: { party: Pick<PublicPoolParty, 'spotsLeft' | 'status'> }) {
  if (party.status === 'SoldOut' || party.spotsLeft === 0) return <span className="pool-spots out">Sold out</span>;
  return <span className={`pool-spots ${party.spotsLeft <= 5 ? 'low' : ''}`}>{party.spotsLeft === 1 ? '1 spot left' : `${party.spotsLeft} spots left`}</span>;
}

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Friday to Sunday of this weekend (the coming one, Monday to Thursday).
const isThisWeekend = (iso: string) => {
  const date = new Date(`${iso}T00:00:00`);
  const friday = new Date();
  friday.setHours(0, 0, 0, 0);
  const day = friday.getDay(); // 0 = Sunday
  friday.setDate(friday.getDate() + (day === 0 ? -2 : day === 6 ? -1 : 5 - day));
  const monday = new Date(friday);
  monday.setDate(friday.getDate() + 3);
  return date >= friday && date < monday;
};

const FILTERS = ['All', 'Today', 'This Weekend', 'Free Entry'] as const;

// /pool-parties: upcoming approved pool parties near the visitor, soonest first.
export function PoolPartiesList() {
  const features = useFeatures();
  const { coords, resolved } = useGeolocation();
  const { area, label } = useLocationFilter();
  const [parties, setParties] = useState<PublicPoolParty[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('All');

  useEffect(() => {
    if (!resolved) return;
    let current = true;
    setFailed(false);
    setParties(null);
    getPoolParties(coords.lat, coords.lng, area).then((list) => current && setParties(list)).catch(() => current && setFailed(true));
    return () => {
      current = false;
    };
  }, [resolved, coords.lat, coords.lng, area]);

  const shown = (parties ?? []).filter((p) =>
    filter === 'All' ? true
      : filter === 'Today' ? p.eventDate === todayIso()
        : filter === 'This Weekend' ? isThisWeekend(p.eventDate)
          : p.pricePerGuest === 0);

  return (
    <main className="container section">
      <LocationFilter />
      <div className="filters">
        {FILTERS.map((f) => (
          <button key={f} type="button" className={`filter ${f === filter ? 'on' : ''}`} aria-pressed={f === filter} onClick={() => setFilter(f)}>{f}</button>
        ))}
        {features.poolPartyBookings && isCustomer() && <Link className="btn btn-dark" to="/profile/bookings" style={{ marginLeft: 'auto' }}>My Bookings</Link>}
      </div>
      {failed ? (
        <div className="panel"><p>Couldn't load pool parties right now. Please refresh the page.</p></div>
      ) : parties === null ? (
        <p className="muted">Loading...</p>
      ) : shown.length === 0 ? (
        <div className="panel"><p className="muted">{parties.length === 0 ? `No pool parties are scheduled ${area.state ? `in ${label}` : 'near you'} yet. Check back soon!` : 'No pool parties match this filter.'}</p></div>
      ) : (
        <div className="grid">
          {shown.map((p) => (
            <article className="card" key={p.id}>
              <div className="card-image">
                <img src={venueImageUrl(p.imageUrl) ?? '/hero.jpg'} alt="" loading="lazy" />
                <span className="badge">{p.poolType.toUpperCase()}</span>
                {p.status === 'SoldOut' && <span className="badge pink" style={{ left: 'auto', right: 10 }}>SOLD OUT</span>}
              </div>
              <div className="card-body">
                <h3>{p.title}</h3>
                <p className="muted">{p.venueName}{p.distanceKm != null ? ` · ${p.distanceKm} km` : ''}</p>
                <p>{partyWhen(p)}</p>
                <div className="pool-meta"><span>{partyPrice(p.pricePerGuest)}</span><Spots party={p} /></div>
                <Link className="btn btn-gold" to={`/pool-parties/${p.id}`}>{p.status === 'SoldOut' ? 'View Details' : 'View & Book'}</Link>
              </div>
            </article>
          ))}
        </div>
      )}
      <p className="muted" style={{ fontSize: 12, marginTop: 24 }}>
        Cheers2Night lists events and reserves your spot; it doesn't sell or deliver alcohol. Entry, payment and drinks are handled by the venue,
        which checks ID at the door. Please drink responsibly and never swim after drinking.
      </p>
    </main>
  );
}

// /pool-parties/:id: full details, and booking for signed-in customers.
export function PoolPartyDetails() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const partyId = Number(id);
  const [party, setParty] = useState<PublicPoolParty | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [booking, setBooking] = useState<PoolPartyBooking | null>(null);
  const [guests, setGuests] = useState(1);
  const [eligible, setEligible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const features = useFeatures();
  // Booking is switched off while Cheers2Night is discovery-only; the party is still listed.
  const canBook = features.poolPartyBookings;
  const signedIn = isCustomer();

  const load = useCallback(async () => {
    try {
      setParty(await getPoolParty(partyId));
    } catch {
      setNotFound(true);
    }
    if (signedIn && canBook) {
      getMyPoolPartyBookings()
        .then((all) => setBooking(all.find((b) => b.poolPartyId === partyId && b.status !== 'Cancelled') ?? null))
        .catch(() => undefined);
    }
  }, [partyId, signedIn, canBook]);

  useEffect(() => {
    load();
  }, [load]);

  const book = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const made = await bookPoolParty(partyId, guests);
      setBooking(made);
      setMsg({ kind: 'ok', text: 'You\'re booked! We\'ve sent your entry code by SMS too.' });
      trackEvent('pool_party_booked', { guests });
      getPoolParty(partyId).then(setParty).catch(() => undefined);
    } catch (err) {
      setMsg({ kind: 'err', text: toApiError(err).message });
      getPoolParty(partyId).then(setParty).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <main className="container section">
        <div className="panel"><h2>This pool party isn't available</h2><p className="muted">It may have ended, sold out and closed, or been cancelled.</p><Link className="btn btn-gold" to="/pool-parties">See other pool parties</Link></div>
      </main>
    );
  }
  if (!party) return <main className="container section"><p className="muted">Loading...</p></main>;

  const maxGuests = Math.max(1, Math.min(party.maxGuestsPerBooking, party.spotsLeft));
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${party.latitude},${party.longitude}`;

  return (
    <main className="container section pool-detail">
      <div className="two-col">
        <div>
          <div className="pool-cover"><img src={venueImageUrl(party.imageUrl) ?? '/hero.jpg'} alt={party.venueName} /></div>
          <p className="gold" style={{ fontWeight: 800, fontSize: 12, letterSpacing: 1 }}>{party.poolType.toUpperCase()}</p>
          <h1>{party.title}</h1>
          <p className="muted">{party.venueName} · {party.address} · <a className="gold" href={mapUrl} target="_blank" rel="noreferrer">Map</a></p>
          <p>{partyWhen(party)}</p>
          {party.description && <><h3>About</h3><p>{party.description}</p></>}
          {party.inclusions && <><h3>What's included</h3><p>{party.inclusions}</p></>}
          {party.agePolicy && <><h3>Age policy</h3><p>{party.agePolicy}</p></>}
          {party.dressCode && <><h3>Dress code</h3><p>{party.dressCode}</p></>}
          {party.safetyRules && <><h3>Safety rules</h3><p>{party.safetyRules}</p></>}
          {party.cancellationPolicy && <><h3>Cancellation policy</h3><p>{party.cancellationPolicy}</p></>}
          {party.emergencyContact && <><h3>Emergency contact</h3><p><a className="gold" href={`tel:${party.emergencyContact.replace(/[^\d+]/g, '')}`}>{party.emergencyContact}</a></p></>}
        </div>

        <aside>
          <div className="panel pool-book">
            <h2 style={{ marginTop: 0 }}>{!canBook ? 'Entry details' : booking ? 'Your booking' : 'Book your spot'}</h2>
            <div className="row"><span className="muted">Price</span><b>{partyPrice(party.pricePerGuest)}</b></div>
            {canBook && <div className="row"><span className="muted">Availability</span><Spots party={party} /></div>}
            {canBook && party.pricePerGuest > 0 && <p className="muted" style={{ fontSize: 12 }}>Booking is free. You pay the venue on arrival.</p>}
            <Message msg={msg} />

            {!canBook ? (
              <>
                <p className="muted" style={{ fontSize: 13 }}>Entry and payment are handled by the venue. Contact them for details.</p>
                <Link className="btn btn-gold" style={{ display: 'block', marginBottom: 10 }} to={`/venues/resto-${party.restoId}`}>Contact the Venue</Link>
                <a className="btn btn-dark" style={{ display: 'block' }} href={`https://www.google.com/maps/dir/?api=1&destination=${party.latitude},${party.longitude}`} target="_blank" rel="noreferrer"
                  onClick={() => trackEvent('directions_click', { source: 'pool_party' })}>Get Directions</a>
              </>
            ) : booking ? (
              <>
                <div className="row"><span className="muted">Guests</span><b>{booking.guests}</b></div>
                {booking.status === 'CheckedIn' ? (
                  <div className="pool-msg ok">Checked in. Enjoy the party!</div>
                ) : (
                  <>
                    <p style={{ marginBottom: 0 }}>Show this entry code at the door:</p>
                    <div className="pool-code" aria-label="Entry code">{booking.code}</div>
                    <p className="muted" style={{ fontSize: 12 }}>Every guest needs a valid photo ID. The code works once.</p>
                  </>
                )}
                <Link className="btn btn-dark" to="/profile/bookings" style={{ display: 'block' }}>My Bookings</Link>
              </>
            ) : party.status === 'SoldOut' ? (
              <div className="pool-msg info">This party is sold out.</div>
            ) : !signedIn ? (
              <>
                <p className="muted" style={{ fontSize: 13 }}>Sign in with your mobile number to book. It takes a minute.</p>
                <Link className="btn btn-gold" style={{ display: 'block' }} to={loginUrlFor(location.pathname)}>Sign in to book</Link>
              </>
            ) : (
              <form onSubmit={book}>
                <label htmlFor="pool-guests" style={{ display: 'block', margin: '12px 0 6px' }}>Guests (including you)</label>
                <select id="pool-guests" className="input" value={guests} onChange={(e) => setGuests(Number(e.target.value))}>
                  {Array.from({ length: maxGuests }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                {party.pricePerGuest > 0 && <div className="row"><span className="muted">Pay at venue</span><b>₹{(party.pricePerGuest * guests).toLocaleString('en-IN')}</b></div>}
                <label>
                  <input type="checkbox" checked={eligible} onChange={(e) => setEligible(e.target.checked)} />
                  <span>Everyone in my group meets the age policy and will carry a valid photo ID. I've read the safety rules.</span>
                </label>
                <button className="btn btn-gold" type="submit" disabled={busy || !eligible} style={{ width: '100%' }}>{busy ? 'Booking...' : 'Book Now'}</button>
                <p className="muted" style={{ fontSize: 11 }}>By booking you agree to the <Link className="gold" to="/terms">Terms</Link>.</p>
              </form>
            )}
          </div>
        </aside>
      </div>
    </main>
  );
}

// /profile/bookings: the signed-in customer's pool-party bookings with their entry codes.
export function MyPoolPartyBookings() {
  const location = useLocation();
  const [bookings, setBookings] = useState<PoolPartyBooking[] | null>(null);
  const [msg, setMsg] = useState<Msg>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const signedIn = isCustomer();

  const load = useCallback(() => getMyPoolPartyBookings().then(setBookings).catch(() => setMsg({ kind: 'err', text: 'Could not load your bookings. Please refresh.' })), []);
  useEffect(() => {
    if (signedIn) load();
  }, [signedIn, load]);

  if (!signedIn) return <Navigate to={loginUrlFor(location.pathname)} replace />;

  const cancel = async (b: PoolPartyBooking) => {
    if (!window.confirm(`Cancel your booking for ${b.partyTitle}? Your spot goes to someone else.`)) return;
    setBusyId(b.id);
    try {
      await cancelPoolPartyBooking(b.id);
      setMsg({ kind: 'ok', text: 'Booking cancelled.' });
      await load();
    } catch (err) {
      setMsg({ kind: 'err', text: toApiError(err).message });
    } finally {
      setBusyId(null);
    }
  };

  const label = (b: PoolPartyBooking) => (b.status === 'CheckedIn' ? 'Checked in' : b.partyCancelled ? 'Party cancelled' : b.status);

  return (
    <main className="container section">
      <Message msg={msg} />
      {bookings === null ? (
        <p className="muted">Loading...</p>
      ) : bookings.length === 0 ? (
        <div className="panel"><p className="muted">You haven't booked any pool parties yet.</p><Link className="btn btn-gold" to="/pool-parties">Find a pool party</Link></div>
      ) : (
        bookings.map((b) => (
          <div className="panel pool-booking" key={b.id}>
            <div>
              <span className={`pool-status ${b.partyCancelled ? 'Cancelled' : b.status}`}>{label(b)}</span>
              <h3 style={{ marginTop: 8 }}><Link to={`/pool-parties/${b.poolPartyId}`}>{b.partyTitle}</Link></h3>
              <p className="muted" style={{ margin: 0 }}>{b.venueName} · {partyWhen(b)}</p>
              <p style={{ margin: '4px 0 0', fontSize: 14 }}>{b.guests === 1 ? '1 guest' : `${b.guests} guests`}{b.pricePerGuest > 0 ? ` · pay ₹${(b.pricePerGuest * b.guests).toLocaleString('en-IN')} at the venue` : ' · free entry'}</p>
              {b.partyCancelled && b.cancelReason && <p className="muted" style={{ fontSize: 13 }}>Host's note: {b.cancelReason}</p>}
              {b.code && <div className="pool-code" aria-label="Entry code">{b.code}</div>}
            </div>
            {b.canCancel && <button className="btn btn-dark" disabled={busyId === b.id} onClick={() => cancel(b)}>{busyId === b.id ? 'Cancelling...' : 'Cancel booking'}</button>}
          </div>
        ))
      )}
    </main>
  );
}
